import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  FluentProvider,
  Link,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  OverlayDrawer,
  ProgressBar,
  Text,
  tokens,
} from '@fluentui/react-components';
import { useActiveTheme } from '../theme/ThemeContext';
import ColorSchemeSwitch from '../theme/ColorSchemeSwitch';
import {
  Navigation24Regular,
  Dismiss24Regular,
  ChevronLeft24Regular,
  SignOut24Regular,
  ChevronDown16Regular,
  ChevronDoubleLeft20Regular,
  ChevronDoubleRight20Regular,
  PersonRegular,
  HeadsetRegular,
} from '@fluentui/react-icons';
import { getInitials } from '@moko/utils';
import MokoCard from './MokoCard';
import ContactSupportForm from './ContactSupportForm';
import { getAccount, msalLogoutRedirect } from '@moko/utils/src/authHelper';
import { msalInstance, msalConfig } from '@moko/utils/src/authConfig';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import RoleSelector from '../RoleSelector/RoleSelector';
import WelcomeMessage from './WelcomeMessage';
import Breadcrumbs from './Breadcrumbs';
import { resolveTrail, type BreadcrumbItem } from './breadcrumbTrail';
import { BreadcrumbContext } from './useBreadcrumbs';
import PageHeader from './PageHeader';
import ConnectionStatus from './ConnectionStatus';
import { connectionState } from './connectionState';
import { useThemedAvatar } from '../theme/useThemedAvatar';
import { useTenant } from '../TenantProvider';
import { readTenantBrand } from './tenantBrand';
import {
  BRAND,
  headerCss,
  navIconColor,
  navItemCss,
  shellVar,
} from './shellTheme';

export interface NavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  iconColor?: string;
  section?: string;
  path: string;
  activePaths?: string[];
  /** When true, AppLayout renders children without the contentCard wrapper and page title. The route controls its own layout. */
  customLayout?: boolean;
}

const BOTTOM_NAV_HEIGHT = 56;

type TenantContactInfo = {
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
};

const SIDEBAR_WIDTH_EXPANDED = 248;
const TOPBAR_HEIGHT = 62;
const SIDEBAR_WIDTH_COLLAPSED = 48;
const SIDEBAR_BRAND_TEXT = shellVar('sidebar-brand-text', BRAND);

const TenantBrandMark = ({
  collapsed = false,
  compact = false,
}: {
  collapsed?: boolean;
  compact?: boolean;
}) => {
  const { tenant } = useTenant();
  const brand = useMemo(
    () => readTenantBrand(tenant?.otherInfo),
    [tenant?.otherInfo]
  );
  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = logoFailed ? undefined : brand.logoUrl;
  const label = brand.title || 'FMS';
  const titleFontSize = collapsed ? 14 : 18;
  const brandTextColor = compact ? BRAND : SIDEBAR_BRAND_TEXT;
  const titleStyle: React.CSSProperties = {
    color: brandTextColor,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ...(collapsed ? undefined : brand.titleStyle),
  };
  const showSubtitle = !collapsed && !compact && Boolean(brand.subtitle);

  useEffect(() => {
    setLogoFailed(false);
  }, [brand.logoUrl]);

  if (collapsed) {
    if (logoUrl) {
      return (
        <img
          src={logoUrl}
          alt={label}
          style={{
            height: 24,
            maxWidth: 32,
            objectFit: 'contain',
            flexShrink: 0,
          }}
          onError={() => setLogoFailed(true)}
        />
      );
    }
    return (
      <strong style={{ fontSize: titleFontSize, color: brandTextColor }}>
        {brand.title ? brand.title.slice(0, 3) : label}
      </strong>
    );
  }

  const showTitleBesideLogo = Boolean(logoUrl && brand.title);
  const showStandaloneTitle = !logoUrl;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={label}
          style={{
            height: 55,
            maxWidth: 72,
            objectFit: 'contain',
            flexShrink: 0,
          }}
          onError={() => setLogoFailed(true)}
        />
      ) : null}
      {showStandaloneTitle || showTitleBesideLogo || showSubtitle ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 4,
            minWidth: 0,
          }}
        >
          {showStandaloneTitle ? (
            <strong style={{ fontSize: titleFontSize, ...titleStyle }}>
              {label}
            </strong>
          ) : showTitleBesideLogo ? (
            <span style={{ fontSize: 16, fontWeight: 600, ...titleStyle }}>
              {brand.title}
            </span>
          ) : null}
          {showSubtitle ? (
            <span
              style={{
                fontSize: 11,
                lineHeight: '16px',
                fontWeight: 400,
                color: brandTextColor,
                opacity: 0.8,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {brand.subtitle}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

type AppLayoutProps = {
  navItems: NavItem[];
  logo?: React.ReactNode;
  isInProgress?: boolean;
  onNavigate: (path: string) => void;
  currentPath: string;
  homePath?: string;
  pageTitle?: string;
  headerContent?: React.ReactNode;
  headerActions?: React.ReactNode;
  /** When set, a "Contact support" item appears in the avatar menu and
   *  navigates here. Omit to hide the item (apps without the page). */
  contactSupportPath?: string;
  children: React.ReactNode;
};

const AppLayout: React.FC<AppLayoutProps> = ({
  navItems,
  logo,
  isInProgress,
  onNavigate,
  currentPath,
  homePath,
  pageTitle,
  headerContent,
  headerActions,
  contactSupportPath,
  children,
}) => {
  const theme = useActiveTheme();
  const avatarClass = useThemedAvatar();
  const { isOnline, isAuthenticated, canWorkOffline, onRetry } =
    useOfflineStatus();
  const account = getAccount();
  const username = account?.name || 'User';
  const initials = useMemo(() => getInitials(username), [username]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isCustomLayout = useMemo(
    () => navItems.some((n) => n.customLayout && n.path === currentPath),
    [navItems, currentPath]
  );
  const [registeredTrail, setRegisteredTrail] = useState<
    BreadcrumbItem[] | null
  >(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profileView, setProfileView] = useState<'profile' | 'contact'>(
    'profile'
  );
  const { tenant } = useTenant();
  const tenantContactInfo = tenant?.otherInfo as TenantContactInfo | undefined;

  const closeProfile = () => {
    setIsProfileOpen(false);
    setProfileView('profile');
  };

  const claims = account?.idTokenClaims as Record<string, string> | undefined;
  const givenName = claims?.given_name ?? account?.name?.split(' ')[0] ?? '';
  const surname =
    claims?.family_name ?? account?.name?.split(' ').slice(1).join(' ') ?? '';
  const upn = account?.username ?? '';
  const accountId = account?.localAccountId ?? '';

  const onSignOut = async () => {
    await msalLogoutRedirect(msalInstance, {
      authority: msalConfig.auth.authority,
      postLogoutRedirectUri: '/',
    });
  };

  const handleNavClick = (item: NavItem) => {
    setMobileNavOpen(false);
    onNavigate(item.path);
  };

  const isNavActive = (item: NavItem) => {
    if (item.path === '/') {
      return (
        currentPath === '/' ||
        !navItems.some((n) => n.path !== '/' && currentPath.startsWith(n.path))
      );
    }
    return (
      currentPath.startsWith(item.path) ||
      (item.activePaths ?? []).some(
        (path) => currentPath === path || currentPath.startsWith(`${path}/`)
      )
    );
  };

  const trail = resolveTrail(
    registeredTrail,
    pageTitle || navItems.find(isNavActive)?.label
  );
  const showWelcome = currentPath === homePath || trail.length === 0;

  const sidebarWidth = sidebarCollapsed
    ? SIDEBAR_WIDTH_COLLAPSED
    : SIDEBAR_WIDTH_EXPANDED;

  const renderSidebarContent = (collapsed: boolean) => (
    <>
      <div
        style={{
          ...styles.logoArea,
          padding: collapsed ? '16px 0' : '16px 20px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          alignItems: 'center',
        }}
        onClick={() => (window.location.href = '/')}
      >
        {logo ?? <TenantBrandMark collapsed={collapsed} />}
      </div>
      <nav style={{ ...styles.nav, padding: collapsed ? '12px 6px' : '12px' }}>
        {navItems.map((item, index) => {
          const active = isNavActive(item);
          const startsSection =
            item.section !== undefined &&
            item.section !== navItems[index - 1]?.section;
          const showSection = startsSection && (!collapsed || index > 0);
          const btn = (
            <button
              key={item.key}
              data-nav-item=""
              data-active={active ? '' : undefined}
              data-collapsed={collapsed ? '' : undefined}
              onClick={() => handleNavClick(item)}
            >
              {item.icon && (
                <span
                  style={{
                    ...styles.navIcon,
                    color: navIconColor(item.iconColor, active),
                  }}
                >
                  {item.icon}
                </span>
              )}
              {!collapsed && <span>{item.label}</span>}
            </button>
          );

          return (
            <React.Fragment key={item.key}>
              {showSection && (
                <div
                  data-nav-section=""
                  data-collapsed={collapsed ? '' : undefined}
                >
                  {collapsed ? null : item.section}
                </div>
              )}
              {btn}
            </React.Fragment>
          );
        })}
      </nav>
      <div
        style={{
          padding: '8px',
          borderTop: `1px solid ${shellVar(
            'sidebar-border',
            tokens.colorNeutralStroke2
          )}`,
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'flex-end',
        }}
      >
        <button
          type="button"
          data-sidebar-toggle=""
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setSidebarCollapsed(!collapsed)}
        >
          {collapsed ? (
            <ChevronDoubleRight20Regular />
          ) : (
            <ChevronDoubleLeft20Regular />
          )}
        </button>
      </div>
    </>
  );

  const mobileSidebarContent = renderSidebarContent(false);

  return (
    <BreadcrumbContext.Provider value={setRegisteredTrail}>
      <style>{cssStyles}</style>
      <div
        data-layout-root=""
        style={
          {
            ...styles.root,
            '--app-sidebar-width': `${sidebarWidth}px`,
          } as React.CSSProperties
        }
      >
        <aside
          data-layout-sidebar=""
          style={{
            ...styles.sidebar,
            width: sidebarWidth,
            minWidth: sidebarWidth,
          }}
        >
          {renderSidebarContent(sidebarCollapsed)}
        </aside>

        {mobileNavOpen && (
          <div
            data-layout-overlay=""
            style={styles.overlay}
            onClick={() => setMobileNavOpen(false)}
          />
        )}
        <aside
          data-layout-mobile-sidebar=""
          style={{
            ...styles.mobileSidebar,
            transform: mobileNavOpen ? 'translateX(0)' : 'translateX(-100%)',
          }}
        >
          <div style={styles.mobileCloseBtn}>
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={() => setMobileNavOpen(false)}
            />
          </div>
          {mobileSidebarContent}
        </aside>

        <div style={styles.rightColumn}>
          <header data-layout-header="" style={styles.header}>
            <button
              data-layout-hamburger=""
              style={styles.hamburger}
              onClick={() => setMobileNavOpen(true)}
            >
              <Navigation24Regular />
            </button>

            <div
              data-layout-mobile-logo=""
              style={styles.mobileLogo}
              onClick={() => (window.location.href = '/')}
            >
              {logo ?? <TenantBrandMark compact />}
            </div>

            {showWelcome ? (
              <WelcomeMessage />
            ) : (
              <Breadcrumbs trail={trail} onNavigate={onNavigate} />
            )}

            <div data-layout-header-end="" style={styles.headerEnd}>
              <div data-layout-header-search="" style={styles.headerSearch}>
                {headerContent}
              </div>
              <div data-layout-header-right="" style={styles.headerRight}>
                {headerActions}
                <ConnectionStatus
                  state={connectionState(
                    isOnline,
                    isAuthenticated,
                    canWorkOffline
                  )}
                />
                {!isOnline && onRetry && (
                  <button
                    onClick={onRetry}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '2px 6px',
                    }}
                  >
                    ↻
                  </button>
                )}
                <RoleSelector />
                <Menu positioning="below-end">
                  <MenuTrigger disableButtonEnhancement>
                    <button data-layout-avatar-button="">
                      <FluentProvider
                        theme={theme}
                        style={{
                          display: 'inline-flex',
                          background: 'transparent',
                        }}
                      >
                        <Avatar
                          initials={{
                            children: initials,
                            className: avatarClass,
                          }}
                          size={32}
                          color="brand"
                        />
                      </FluentProvider>
                      <span data-layout-username="" style={styles.username}>
                        {username}
                      </span>
                      <ChevronDown16Regular
                        style={{ color: tokens.colorNeutralForeground3 }}
                      />
                    </button>
                  </MenuTrigger>
                  <MenuPopover
                    style={{
                      borderRadius: 8,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
                      border: `1px solid ${tokens.colorNeutralStroke1}`,
                    }}
                  >
                    <FluentProvider
                      theme={theme}
                      style={{ background: 'transparent' }}
                    >
                      <MenuList>
                        <MenuItem
                          icon={<PersonRegular />}
                          onClick={() => setIsProfileOpen(true)}
                        >
                          Profile
                        </MenuItem>
                        {contactSupportPath && (
                          <MenuItem
                            icon={<HeadsetRegular />}
                            onClick={() => onNavigate(contactSupportPath)}
                          >
                            Contact support
                          </MenuItem>
                        )}
                        <div
                          style={{
                            boxSizing: 'border-box',
                            width: '100%',
                            minWidth: 0,
                            padding: '8px 12px',
                          }}
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <ColorSchemeSwitch fullWidth />
                        </div>
                        <MenuItem
                          icon={<SignOut24Regular />}
                          onClick={onSignOut}
                        >
                          Sign out
                        </MenuItem>
                      </MenuList>
                    </FluentProvider>
                  </MenuPopover>
                </Menu>
              </div>
            </div>
          </header>

          <div style={styles.progressBarContainer}>
            <ProgressBar
              value={isInProgress ? undefined : 1}
              thickness="large"
              color="brand"
              style={{ height: 3 }}
            />
          </div>

          <main data-layout-main="" style={styles.main}>
            {isCustomLayout || !pageTitle ? (
              children
            ) : (
              <div style={styles.titledPage}>
                <PageHeader title={pageTitle} />
                <MokoCard>{children}</MokoCard>
              </div>
            )}
          </main>
        </div>

        <nav data-layout-bottom-nav="" style={styles.bottomNav}>
          {navItems.map((item) => {
            const active = isNavActive(item);
            return (
              <button
                key={item.key}
                data-bottom-nav-item=""
                onClick={() => handleNavClick(item)}
                style={styles.bottomNavButton}
              >
                <span
                  style={{
                    fontSize: 22,
                    color:
                      item.iconColor ??
                      (active ? BRAND : tokens.colorNeutralForeground3),
                  }}
                >
                  {item.icon}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: active ? 600 : 400,
                    color: active ? BRAND : tokens.colorNeutralForeground3,
                  }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      <FluentProvider theme={theme}>
        <OverlayDrawer
          open={isProfileOpen}
          onOpenChange={(_, data) => {
            if (!data.open) {
              closeProfile();
            }
          }}
          position="end"
          size="small"
        >
          <DrawerHeader>
            <DrawerHeaderTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  onClick={closeProfile}
                />
              }
            >
              {profileView === 'contact' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Button
                    appearance="subtle"
                    aria-label="Back"
                    icon={<ChevronLeft24Regular />}
                    onClick={() => setProfileView('profile')}
                  />
                  Contact support
                </div>
              ) : (
                'Profile'
              )}
            </DrawerHeaderTitle>
          </DrawerHeader>
          <DrawerBody>
            {profileView === 'contact' ? (
              <ContactSupportForm onBack={() => setProfileView('profile')} />
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  paddingTop: '8px',
                }}
              >
                <div>
                  <Text
                    weight="semibold"
                    style={{ display: 'block', marginBottom: 2 }}
                  >
                    First Name
                  </Text>
                  <Text>{givenName}</Text>
                </div>
                <div>
                  <Text
                    weight="semibold"
                    style={{ display: 'block', marginBottom: 2 }}
                  >
                    Last Name
                  </Text>
                  <Text>{surname}</Text>
                </div>
                <div>
                  <Text
                    weight="semibold"
                    style={{ display: 'block', marginBottom: 2 }}
                  >
                    Username
                  </Text>
                  <Text>{upn}</Text>
                </div>
                <div>
                  <Text
                    weight="semibold"
                    style={{ display: 'block', marginBottom: 2 }}
                  >
                    Email
                  </Text>
                  <Text>{upn}</Text>
                </div>
                <div>
                  <Text
                    weight="semibold"
                    style={{ display: 'block', marginBottom: 2 }}
                  >
                    Id
                  </Text>
                  <Text>{accountId}</Text>
                </div>
                <div>
                  <Text
                    weight="semibold"
                    style={{ display: 'block', marginBottom: 2 }}
                  >
                    Contact Name
                  </Text>
                  <Text>{tenantContactInfo?.contactName || '-'}</Text>
                </div>
                <div style={{ paddingTop: 8 }}>
                  <Link
                    onClick={() => {
                      if (contactSupportPath) {
                        closeProfile();
                        onNavigate(contactSupportPath);
                      } else {
                        setProfileView('contact');
                      }
                    }}
                  >
                    Need help?
                  </Link>
                </div>
              </div>
            )}
          </DrawerBody>
        </OverlayDrawer>
      </FluentProvider>
    </BreadcrumbContext.Provider>
  );
};

const styles: Record<string, React.CSSProperties> = {
  root: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    backgroundColor: shellVar(
      'page-background',
      tokens.colorNeutralBackground2
    ),
  },
  sidebar: {
    display: 'none',
    flexDirection: 'column',
    background: shellVar('sidebar-background', tokens.colorNeutralBackground1),
    boxShadow: '2px 0 6px rgba(0,0,0,0.06)',
    zIndex: 10,
    transition: 'width 0.2s ease-in-out, min-width 0.2s ease-in-out',
  },
  logoArea: {
    display: 'flex',
    padding: '16px 20px',
    cursor: 'pointer',
    borderBottom: `1px solid ${shellVar(
      'sidebar-border',
      tokens.colorNeutralStroke2
    )}`,
    overflow: 'hidden',
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    overflow: 'hidden',
  },
  navIcon: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 20,
  },
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
    zIndex: 998,
  },
  mobileSidebar: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    bottom: 0,
    width: SIDEBAR_WIDTH_EXPANDED,
    background: shellVar('sidebar-background', tokens.colorNeutralBackground1),
    zIndex: 999,
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease',
    boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
  },
  mobileCloseBtn: {
    position: 'absolute' as const,
    top: 12,
    right: 8,
    zIndex: 1,
  },
  rightColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px',
    height: TOPBAR_HEIGHT,
    minHeight: TOPBAR_HEIGHT,
    backgroundColor: shellVar(
      'topbar-background',
      tokens.colorNeutralBackground1
    ),
    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
    zIndex: 5,
    gap: 8,
  },
  progressBarContainer: {
    height: 3,
    flexShrink: 0,
  },
  hamburger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
  },
  mobileLogo: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
  },
  headerEnd: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    gap: 8,
  },
  headerSearch: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    padding: '0 12px',
  },
  username: {
    fontSize: 13,
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  main: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  },
  titledPage: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  bottomNav: {
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    right: 0,
    height: BOTTOM_NAV_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    zIndex: 200,
  },
  bottomNavButton: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 12px',
    minWidth: 56,
  },
};

const cssStyles = `
  :root {
    --app-sidebar-width: 0px;
    --app-bottom-nav-height: ${BOTTOM_NAV_HEIGHT}px;
  }

  ${navItemCss}
  ${headerCss}


  @media (max-width: 768px) {
    [data-layout-header] {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas:
        "logo actions"
        "context context";
      height: auto !important;
      min-height: 48px;
      padding: 8px 12px 10px !important;
      column-gap: 8px;
      row-gap: 2px;
      align-items: center;
    }
    [data-layout-mobile-logo] {
      grid-area: logo;
    }
    [data-layout-context] {
      grid-area: context;
      max-width: 100% !important;
    }
    [data-layout-header-end] {
      grid-area: actions;
      flex: 0 0 auto !important;
    }
    [data-layout-header-search] {
      flex: 0 0 auto !important;
      min-width: 0 !important;
      padding: 0 !important;
    }
    [data-layout-header-right] {
      gap: 6px !important;
    }
    [data-layout-main] {
      padding-bottom: ${BOTTOM_NAV_HEIGHT + 16}px !important;
    }
    [data-layout-username] {
      display: none !important;
    }
    [data-layout-hamburger] {
      display: none !important;
    }
    [data-layout-mobile-sidebar] {
      display: none !important;
    }
    [data-layout-overlay] {
      display: none !important;
    }
  }

  @media (min-width: 769px) and (max-width: 1199px) {
    [data-layout-header] {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr);
      grid-template-areas:
        "actions"
        "context";
      height: auto !important;
      min-height: 48px;
      padding: 8px 16px 10px !important;
      row-gap: 2px;
      align-items: center;
    }
    [data-layout-header-end] {
      grid-area: actions;
    }
    [data-layout-context] {
      grid-area: context;
      max-width: none !important;
      overflow: visible !important;
    }
    [data-layout-username] {
      display: none !important;
    }
  }

  @media (min-width: 1200px) {
    [data-layout-context] {
      flex: 0 1 auto;
      min-width: 0;
    }
  }

  @media (min-width: 769px) {
    :root {
      --app-bottom-nav-height: 0px;
    }
    [data-layout-sidebar] {
      display: flex !important;
    }
    [data-layout-hamburger] {
      display: none !important;
    }
    [data-layout-mobile-logo] {
      display: none !important;
    }
    [data-layout-mobile-sidebar] {
      display: none !important;
    }
    [data-layout-overlay] {
      display: none !important;
    }
    [data-layout-bottom-nav] {
      display: none !important;
    }
  }
`;

export default AppLayout;
